import React, { useEffect, useState } from 'react';
import { Container, Stack } from '@fiverr-private/layout_components';
import { translate } from '@fiverr-private/i18n-react';
import { Combobox, ComboboxOption, ComboboxOptionGroup, ComboboxOptionLabel } from '@fiverr-private/forms';
import { Values } from '@fiverr-private/dropdown';
import { useIdealCandidateContext } from '../../../utils/context';
import { T_TIMEZONE_PREFIX } from '../../../utils/consts';
import { useTimezone } from './useTimezone';

const TimezoneRegion = ({ region, setRegion }: { region?: string; setRegion: (value: string) => void }) => {
  const [inputValue, setInputValue] = useState<string | undefined>('');
  const [selectedTimezone, setSelectedTimezone] = useState<Values>({ item: region || '' });
  const { timezoneOptions, mappedTimezones } = useTimezone();

  const handleChange = (value: Values) => {
    setSelectedTimezone(value);
    setRegion(value.item ? String(value.item) : '');
  };

  useEffect(() => {
    if (!region) {
      setInputValue('');
      setSelectedTimezone({ item: '' });
    } else {
      setSelectedTimezone({ item: region });
      setInputValue(mappedTimezones[region]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region]);

  return (
    <Combobox
      value={selectedTimezone}
      onChange={handleChange}
      inputValue={inputValue}
      onInputValueChange={setInputValue}
      width="100%"
      placeholder={translate(`${T_TIMEZONE_PREFIX}.timezone_placeholder`) as string}
    >
      <ComboboxOptionGroup>
        {timezoneOptions.map((timezone) => (
          <ComboboxOption value={timezone.value} key={timezone.value}>
            <ComboboxOptionLabel>{timezone.label}</ComboboxOptionLabel>
          </ComboboxOption>
        ))}
      </ComboboxOptionGroup>
    </Combobox>
  );
};

const TimezoneOffset = ({ offset, setOffset }: { offset?: number; setOffset: (value: number) => void }) => {
  const [state, setState] = useState<Values>({ item: `${offset}` });
  const offsets = Array.from({ length: 10 }, (_, i) => i);
  const getLabel = (offset: number) =>
    `-/+ ${offset} ${offset === 1 ? translate(`${T_TIMEZONE_PREFIX}.hour`) : translate(`${T_TIMEZONE_PREFIX}.hours`)}`;

  return (
    <Combobox
      value={state}
      onChange={(value) => {
        setState(value);
        setOffset(Number(value.item));
      }}
      width="100%"
      hideClearButton
    >
      {offsets.map((offset) => (
        <ComboboxOption value={`${offset}`} key={offset}>
          <ComboboxOptionLabel>{getLabel(offset)}</ComboboxOptionLabel>
        </ComboboxOption>
      ))}
    </Combobox>
  );
};

export const Timezone = () => {
  const { timezone, setTimezone } = useIdealCandidateContext();
  return (
    <Stack direction={{ default: 'column', sm: 'row' }} width="100%">
      <Container width="100%" maxWidth="550px">
        <TimezoneRegion
          region={timezone?.timezoneId}
          setRegion={(value) => setTimezone({ ...timezone, timezoneId: value })}
        />
      </Container>
      <Container width={{ default: '100%', sm: '160px' }}>
        <TimezoneOffset
          offset={timezone?.maxOffsetHours || 1}
          setOffset={(value) => setTimezone({ ...timezone, maxOffsetHours: value })}
        />
      </Container>
    </Stack>
  );
};
